import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'

import RadioGroup from '@mui/material/RadioGroup'

import FormInput from '../inputFields/FormInput'
import MaskedInput from '../inputFields/MaskedInput'
import { config } from '../../config'
import { theme } from '../../themes/theme'
import { FlexColumn, FormRow, MedBGContainer } from '../styles/Containers'
import { RowFormControl, StyledFormControlLabel, StyledRadio } from '../styles/RadioGroup'
import { InfoMessage } from '../common/UserMessage'
import BodyText, { Subheading, FormTitle, ErrorText, BoldSpan } from '../styles/Text'
import UploadFile from '../files/UploadFile'
import BrowserFile from '../files/BrowserFile'

const Red = styled.span`
  color: ${theme.colors.error};
`

const Link = styled.a`
  color: ${theme.colors.text.link};
`

function OwnerForm({ loading = false, variant = 'public' }) {
  const { register, watch, formState, setValue, getValues } = useFormContext()
  const [defaultOwnedBy, setDefaultOwnedBy] = useState()
  const [defaultSignatureSource, setDefaultSignatureSource] = useState()

  useEffect(() => {
    setDefaultOwnedBy(getValues('ownerInformation.ownedBy') || '')
    setDefaultSignatureSource(getValues('ownerInformation.signatureSource') || '')
  }, [])

  const ownedBy = watch('ownerInformation.ownedBy')
  const signatureSource = watch('ownerInformation.signatureSource')
  const file = watch('ownerInformation.signatureFile')

  const sigSourceError = !!formState.errors.ownerInformation?.signatureSource
  const sigSourceHelperText = formState.errors?.ownerInformation?.signatureSource?.message

  const sigFileError = !!formState.errors.ownerInformation?.signatureFile
  const sigFileHelperText = formState.errors?.ownerInformation?.signatureFile?.message

  const handleUploadFiles = acceptedFiles => setValue('ownerInformation.signatureFile', acceptedFiles[0])
  const handleDelete = () => setValue('ownerInformation.signatureFile', undefined)

  return (
    <FlexColumn>
      <FormTitle>Owner Information</FormTitle>
      {variant !== 'staff' && <InfoMessage>Only one property owner is required to authorize this request.</InfoMessage>}
      {defaultOwnedBy !== undefined && (
        <FlexColumn gap="0">
          <RowFormControl>
            <BodyText>
              Who owns this property?<Red> *</Red>
            </BodyText>
            <RadioGroup row name="owned-by-radio-group" id="ownerInformation.ownedBy" defaultValue={defaultOwnedBy}>
              <StyledFormControlLabel
                {...register('ownerInformation.ownedBy')}
                value="person"
                control={<StyledRadio />}
                label="Person"
                disabled={loading}
              />
              <StyledFormControlLabel
                {...register('ownerInformation.ownedBy')}
                value="company"
                control={<StyledRadio />}
                label="Company"
                disabled={loading}
              />
            </RadioGroup>
          </RowFormControl>
        </FlexColumn>
      )}
      {ownedBy === 'company' && (
        <FlexColumn gap="10px">
          <Subheading>Company Details</Subheading>
          <FormRow>
            <FormInput required={true} id="ownerInformation.companyName" label="Company Name" disabled={loading} />
          </FormRow>
        </FlexColumn>
      )}
      <FlexColumn gap="10px">
        {ownedBy === 'company' && <Subheading>Authorized Signer Information</Subheading>}
        <FormRow>
          <FormInput required={true} id="ownerInformation.firstName" label="First Name" disabled={loading} />
          <FormInput required={true} id="ownerInformation.lastName" label="Last Name" disabled={loading} />
          <FormInput required={true} id="ownerInformation.address" label="Address" disabled={loading} />
        </FormRow>
      </FlexColumn>
      <FlexColumn gap="10px">
        <Subheading>Owner Authorization</Subheading>
        {defaultSignatureSource !== undefined && (
          <FlexColumn gap="0">
            <RowFormControl>
              <BodyText>
                How will you provide authorization?<Red> *</Red>
              </BodyText>
              <RadioGroup row name="owner-authorization-radio-group" id="ownerInformation.signatureSource" defaultValue={defaultSignatureSource}>
                <StyledFormControlLabel
                  {...register('ownerInformation.signatureSource')}
                  value="upload"
                  control={<StyledRadio />}
                  label="Upload signed document"
                  disabled={loading}
                />
                <StyledFormControlLabel
                  {...register('ownerInformation.signatureSource')}
                  value="electronic"
                  control={<StyledRadio />}
                  label="Digital signature"
                  disabled={loading}
                />
              </RadioGroup>
            </RowFormControl>
            {sigSourceError && <ErrorText role="alert">{sigSourceHelperText}</ErrorText>}
          </FlexColumn>
        )}
        {signatureSource === 'upload' &&
          (file ? (
            <FlexColumn gap="12px">
              <BodyText>The below file will be uploaded as the owner’s signed authorization.</BodyText>
              <MedBGContainer padding="5px 20px 5px 5px">
                <BrowserFile file={file} onDelete={handleDelete} loading={loading} />
              </MedBGContainer>
            </FlexColumn>
          ) : (
            <FlexColumn gap="12px">
              <BodyText>Drag and drop or browse below to upload the signed authorization.</BodyText>
              <UploadFile onUpload={handleUploadFiles} maxFiles={1} />
              {variant !== 'staff' && (
                <InfoMessage>
                  Accepted documents include <BoldSpan>a)</BoldSpan> MLS Listing Contract or <BoldSpan>b)</BoldSpan>{' '}
                  <Link href={config.pir.ownerAuthForm} target="_blank" rel="noopener noreferrer">
                    {config.pir.fullName} Authorization Form
                  </Link>
                  .
                </InfoMessage>
              )}
              {sigFileError && <ErrorText role="alert">{sigFileHelperText}</ErrorText>}
            </FlexColumn>
          ))}

        {signatureSource === 'electronic' && (
          <FlexColumn gap="10px">
            {variant !== 'staff' && (
              <InfoMessage>
                Please provide the {ownedBy === 'company' ? "authorized signer's" : "owner's"} contact information. A digital signature request will
                be sent to the {ownedBy === 'company' ? "authorized signer's" : "owner's"} email to authorize this request.
              </InfoMessage>
            )}

            <FormRow>
              <MaskedInput required={true} id="ownerInformation.phone" label="Phone" format="(###) ###-####" disabled={loading} />
              <FormInput
                required={true}
                id="ownerInformation.email"
                label="Email"
                disabled={loading}
                tooltip="A digital signature request will be sent to this email to authorize this request."
              />
              <FormInput required={true} id="ownerInformation.confirmEmail" label="Confirm Email" disabled={loading} />
            </FormRow>
          </FlexColumn>
        )}
      </FlexColumn>
    </FlexColumn>
  )
}

OwnerForm.propTypes = {
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['public', 'staff'])
}

export default OwnerForm
